/**
 * @lines98/client/lib/board-area-fit — ужать СТАТИЧНУЮ доску-снимок под доступную
 * область экрана, когда у экрана есть ДОПОЛНИТЕЛЬНЫЙ обязательный ряд под доской,
 * которого нет на игровой доске (напр. блок добычи меты на result-screen).
 *
 * WHY THIS EXISTS (SS-1509-class silent-overlap fix, result-reward 320×480/768×1024):
 *   `--cell-size` — ЕДИНЫЙ эталон доски на :root (viewport-sizer.ts), общий для ВСЕХ
 *   экранов (board / daily / replay / result). Его портретный chrome-бюджет
 *   резервирует вертикаль под header + secondary + actions, но НЕ под reward-ряд
 *   (его нет на board/daily/replay). На width-bound портрете (320×480 aspect 0.667,
 *   768×1024 aspect 0.75) доска идёт по ширине → становится высокой → ПЕРЕПОЛНЯЕТ
 *   свой `1fr`-трек и НАЕЗЖАЕТ на reward-ряд (доска позже в paint-порядке → reward
 *   визуально перекрыт, хотя его bounding-box формально в координатах — класс
 *   SS-1509: assertElementsVisible проходит, картинка сломана).
 *
 *   Глобально ужимать --cell-size НЕЛЬЗЯ — это ужало бы ИГРОВУЮ доску (board/daily/
 *   replay), у которых reward-ряда нет (board-as-etalon, §2.1.8). Но доска на result —
 *   СТАТИЧНЫЙ снимок (board-view static mode): её МОЖНО отрисовать меньше (P1 visibility
 *   reward > P4 board-max-size, responsive-ui §0.A). Этот модуль ставит
 *   SCOPED `--cell-size` ТОЛЬКО на host доски-снимка → frame ужимается, эталон :root
 *   и игровая доска не трогаются.
 *
 * МЕХАНИЗМ (измерение, НЕ угаданные коэффициенты — responsive-ui §8 «размер измерением»):
 *   1. Снять scoped override (вернуть host к эталонному --cell-size :root).
 *   2. Измерить фактический рендер frame (`offsetWidth/Height` доски-frame) при
 *      эталонном cell + доступную область host (его `clientWidth/Height` = размер
 *      board-track постамента, который reward-ряд уже урезал как auto-row).
 *   3. scale = min(availH/frameH, availW/frameW, 1) — ТОЛЬКО ужимаем (никогда не
 *      больше эталона; min(...,1) сохраняет board-as-etalon потолок).
 *   4. scopedCell = floor(rootCell · scale), ≥1. Поставить на host — frame ужмётся
 *      пропорционально (все размеры внутри frame — доли --cell-size, инвариантны
 *      при scaling, board.css §150). reward-ряд (sibling, НЕ потомок host) держит
 *      эталонный --cell-size :root → reward НЕ ужимается, только доска.
 *
 *   floor до целого px → чёткая сетка (как viewport-sizer). Пропорция frame к cell
 *   почти линейна на result-размерах (max()-флоры в board-геометрии включаются лишь
 *   при крошечном cell, которого тут нет) → один проход даёт корректный fit; за
 *   подстраховку берём небольшой запас (FIT_EPS) на sub-pixel/floor-погрешность.
 *
 * SCOPE: ТОЛЬКО stacked-раскладка (портрет). В rail (ландшафт) доска — глобальный
 *   true-center эталон (постамент §14.10), reward живёт в rail-колонке сбоку, а НЕ
 *   под доской → доску ужимать НЕ нужно и НЕЛЬЗЯ (detach снимает override).
 *
 * Перерасчёт: на VIEWPORT_APPLIED_EVENT (sizer переписал --cell-size/раскладку) +
 *   ResizeObserver на host (его размер меняется когда reward-ряд меняет высоту) +
 *   document.fonts.ready (frame-плашка с цифрами зависит от шрифта).
 */

import { VIEWPORT_APPLIED_EVENT } from './viewport-sizer.js';

/**
 * Допуск «доска влезает» (px). Доска-снимок при эталонном cell часто ЗАПОЛНЯЕТ свою
 * область с точностью до sub-pixel (width-bound board: frameW ≈ availW). Ужимать её в
 * этом случае НЕЛЬЗЯ — иначе на result-экранах БЕЗ reward-ряда (casual/daily/all_time)
 * доска становилась бы на ~1px меньше эталона зря (регресс чистых baseline'ов). Поэтому
 * scoped-override включается ТОЛЬКО при РЕАЛЬНОМ переполнении (frame больше области
 * больше чем на этот допуск). 1px = floor/sub-pixel слабина (та же конвенция, что у
 * assertElementsVisible).
 */
const FIT_TOLERANCE_PX = 1;

export interface BoardAreaFitHandle {
  /** Принудительный пересчёт (напр. после монтирования reward-ряда). */
  refit(): void;
  detach(): void;
}

/** Прочитать эталонный --cell-size с :root (px). 0 если не задан/не число. */
function readRootCell(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--cell-size');
  const v = parseFloat(raw);
  return Number.isFinite(v) ? v : 0;
}

/** Текущая раскладка постамента (stacked|rail) с :root. */
function currentLayout(): string {
  return document.documentElement.dataset.layout ?? 'stacked';
}

/**
 * Привязать fit-доски к host'у доски-снимка result-экрана.
 *
 * @param host     область доски (`.result-board-area` / `.postament-board`) — её
 *                 clientWidth/Height = доступная board-track постамента.
 * @param frameSel селектор frame доски внутри host (по умолчанию `.board-frame`).
 */
export function attachBoardAreaFit(
  host: HTMLElement,
  frameSel = '.board-frame',
): BoardAreaFitHandle {
  let raf = 0;

  const apply = (): void => {
    raf = 0;
    // В rail доску НЕ ужимаем (она — глобальный true-center эталон; reward в rail-
    // колонке сбоку, не под доской). Снимаем любой scoped override → эталон :root.
    if (currentLayout() !== 'stacked') {
      host.style.removeProperty('--cell-size');
      return;
    }
    const frame = host.querySelector<HTMLElement>(frameSel);
    const rootCell = readRootCell();
    if (!frame || rootCell <= 0) {
      host.style.removeProperty('--cell-size');
      return;
    }
    // Шаг 1: измеряем frame при ЭТАЛОННОМ cell (снять прежний scoped override).
    host.style.removeProperty('--cell-size');
    // Доступ к offset* синхронен → браузер пересчитает layout после removeProperty.
    const frameW = frame.offsetWidth;
    const frameH = frame.offsetHeight;
    const availW = host.clientWidth;
    const availH = host.clientHeight;
    if (frameW <= 0 || frameH <= 0 || availW <= 0 || availH <= 0) return;
    // Шаг 3a: доска ВЛЕЗАЕТ (в пределах допуска) → НЕ ужимаем (board-as-etalon: на
    // result-экранах без reward-ряда доска = эталонной игровой). Override снят выше.
    if (frameH <= availH + FIT_TOLERANCE_PX && frameW <= availW + FIT_TOLERANCE_PX) {
      return;
    }
    // Шаг 3b: РЕАЛЬНОЕ переполнение → scale (только ужимаем; min(...,1) — потолок).
    const scale = Math.min(availH / frameH, availW / frameW, 1);
    // Шаг 4: scoped cell (floor целого px, ≥1). Доска-frame ужмётся пропорционально;
    // reward-ряд (sibling) держит эталонный :root --cell-size → НЕ ужимается.
    const scopedCell = Math.max(1, Math.floor(rootCell * scale));
    host.style.setProperty('--cell-size', `${scopedCell}px`);
  };

  const schedule = (): void => {
    if (raf) return;
    raf = requestAnimationFrame(apply);
  };

  // Первичный замер.
  schedule();

  // Размер host меняется когда reward-ряд (auto) меняет высоту → пересчёт.
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(schedule);
    ro.observe(host);
  }

  // Главный триггер: sizer переписал --cell-size / data-layout (resize/rotate/
  // первая загрузка) → пере-fit под новый эталон.
  const onGeom = (): void => schedule();
  if (typeof window !== 'undefined') {
    window.addEventListener(VIEWPORT_APPLIED_EVENT, onGeom);
  }

  // Плашка доски (цифры счёта/ходов) зависит от шрифта → пере-замер после загрузки.
  if (typeof document !== 'undefined' && 'fonts' in document) {
    void document.fonts.ready.then(schedule);
  }

  return {
    refit: schedule,
    detach(): void {
      if (raf) cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      if (typeof window !== 'undefined') {
        window.removeEventListener(VIEWPORT_APPLIED_EVENT, onGeom);
      }
      host.style.removeProperty('--cell-size');
    },
  };
}
